(ns common.rules
  "Shared interpreter for the field/slot conditional DSL — one operator vocabulary
   and one evaluation policy behind both `common.visibility` and `common.mandatory`,
   so a given rule means the same thing whether it gates visibility or mandatoriness.

   A rule is either a leaf `{:query-type :query-type/single-item :operator … :field …
   :value …}` or a `:query-type/and-collection`/`:or-collection` wrapping nested
   `:rules`. `rules-pass?` preserves the legacy visibility/mandatoriness contract:
   the first top-level rule is the condition; use an explicit collection rule for
   AND/OR semantics."
  (:require
   [clojure.string :as string]
   [common.data-types :as data-types]
   [common.util :as cu]))

(defn- value-set
  "The field value as a membership set. A string scalar is EDN-parsed first, so a
   value persisted as a string (e.g. \":foo\") matches its real keyword/number."
  [fld-val]
  (cond
    (string? fld-val) #{(try (data-types/parse-edn-string fld-val)
                             (catch #?(:clj Exception :cljs :default) _
                               fld-val))}
    (coll? fld-val)   (set fld-val)
    :else             #{fld-val}))

(defn eval-rule
  "Evaluate one rule against `value-lookup`, recursing through and/or collections.
   An unknown operator/query-type is a config bug — it logs and resolves truthy so
   the field surfaces rather than silently vanishing."
  [value-lookup {:keys [query-type operator field value] :as rule}]
  (case query-type
    :query-type/and-collection (every? (partial eval-rule value-lookup) (:rules rule))
    :query-type/or-collection  (boolean (some (partial eval-rule value-lookup) (:rules rule)))
    :query-type/single-item
    (let [fld-val (get value-lookup field)]
      (case operator
        :operand/known                 (not (cu/blank-value? fld-val))
        :operand/unknown               (cu/blank-value? fld-val)
        :operand/any-of                (boolean (some (value-set fld-val) value))
        :operand/all-of                (every? (value-set fld-val) value)
        :operand/none-of               (not-any? (value-set fld-val) value)
        :operand/greater-than          (> fld-val value)
        :operand/smaller-than          (< fld-val value)
        :operand/equal-or-greater-than (>= fld-val value)
        :operand/equal-or-smaller-than (<= fld-val value)
        :operand/after                 (> fld-val value)
        :operand/before                (< fld-val value)
        :operand/starts-with           (string/starts-with? (cu/str-trim-lcase value) (cu/str-trim-lcase fld-val))
        :operand/contains-exactly      (= (cu/str-trim-lcase value) (cu/str-trim-lcase fld-val))
        :operand/equals                (= (cu/str-trim-lcase value) (cu/str-trim-lcase fld-val))
        :operand/true                  (true? fld-val)
        :operand/false                 (false? fld-val)
        (do (println "Unknown rule operator" operator "\n" rule)
            true)))
    (do (println "Unknown rule query-type" "\n" rule)
        true)))

(defn rules-pass?
  "True iff `rules` is non-empty and its first rule passes. Empty/nil ⇒ false, so
   a `:conditional` field/slot with no rules resolves to the negative. This
   preserves legacy `common.visibility` / `common.mandatory` behavior; use
   explicit `:query-type/and-collection` or `:or-collection` for multi-rule
   semantics."
  [value-lookup rules]
  (boolean (and (seq rules)
                (eval-rule value-lookup (first rules)))))
